<script setup>
import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useBranding } from 'shared/composables/useBranding';

import Button from 'dashboard/components-next/button/Button.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import IntegrationLogo from 'dashboard/components-next/integrations/IntegrationLogo.vue';

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
  // Conectores custom (SiteUP): rota nomeada própria + ícone emoji + sem badge de
  // status (não têm hook). Defaults mantêm o comportamento padrão das integrações.
  routeName: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  iconEmoji: {
    type: String,
    default: '',
  },
  showStatus: {
    type: Boolean,
    default: true,
  },
});

const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const integrationStatus = computed(() =>
  props.enabled
    ? t('INTEGRATION_APPS.STATUS.ENABLED')
    : t('INTEGRATION_APPS.STATUS.DISABLED')
);

const integrationStatusColor = computed(() =>
  props.enabled ? 'teal' : 'slate'
);

const actionURL = computed(() =>
  frontendURL(`accounts/${accountId.value}/settings/integrations/${props.id}`)
);

// Conectores custom apontam pra rota nomeada própria; padrão usa a página de hooks.
const linkTarget = computed(() =>
  props.routeName ? { name: props.routeName } : actionURL.value
);
</script>

<template>
  <div
    class="flex flex-col flex-1 p-4 m-px outline outline-n-container outline-1 bg-n-card rounded-2xl"
  >
    <div class="flex items-start justify-between">
      <div class="flex items-center justify-center mb-2">
        <span
          v-if="icon"
          :class="icon"
          class="size-7 text-n-slate-11"
          aria-hidden="true"
        />
        <span v-else-if="iconEmoji" class="text-3xl leading-none">{{
          iconEmoji
        }}</span>
        <IntegrationLogo v-else :integration-id="id" :name="name" />
      </div>
      <Label
        v-if="showStatus"
        :label="integrationStatus"
        :color="integrationStatusColor"
        compact
      />
    </div>
    <div class="flex flex-col m-0 flex-1">
      <div
        class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
      >
        <span class="text-heading-3 text-n-slate-12">{{ name }}</span>
        <router-link :to="linkTarget">
          <Button
            :label="$t('INTEGRATION_APPS.CONFIGURE')"
            icon="i-ph-gear"
            link
            xs
          />
        </router-link>
      </div>
      <p class="text-n-slate-11 text-body-main">
        {{ replaceInstallationName(description) }}
      </p>
    </div>
  </div>
</template>
